import React from 'react';
import {
  Box,
  Button,
  Typography,
} from '@mui/material';

import strings from '../../../localization';

import ConversationComposer from './ConversationComposer';
import ConversationHeader from './ConversationHeader';
import ConversationMessages from './ConversationMessages';
import { getConversationUiState } from './conversationUi';
import styles from './styles';
import { WorkflowDynamicVariables } from './types';
import useAiConversationSession from '../../../hooks/AiOnboarding/useAiConversationSession';
import useConversationComposer from '../../../hooks/AiOnboarding/useConversationComposer';
import useConversationTranscript from '../../../hooks/AiOnboarding/useConversationTranscript';

interface IAiCompanionConversation {
  error: string;
  getDynamicVariables: () => Promise<WorkflowDynamicVariables>;
  isClientToolProcessing: boolean;
  isInteractionPaused: boolean;
  isVoiceMode: boolean;
  onBack: () => void;
  onConversationConnected: (conversationId: string) => void;
  onConversationEnded: () => boolean;
  onErrorChange: (message: string) => void;
  onFallbackToManual: () => void;
  onSwitchToManual: () => void;
  onVoiceModeChange: (isVoiceMode: boolean) => void;
  signupRequestId: string;
}

const AiCompanionConversation = ({
  error,
  getDynamicVariables,
  isClientToolProcessing,
  isInteractionPaused,
  isVoiceMode,
  onBack,
  onConversationConnected,
  onConversationEnded,
  onErrorChange,
  onFallbackToManual,
  onSwitchToManual,
  onVoiceModeChange,
  signupRequestId,
}: IAiCompanionConversation) => {
  const transcript = useConversationTranscript();
  const session = useAiConversationSession({
    getDynamicVariables,
    isInteractionPaused,
    isVoiceMode,
    onBack,
    onConversationConnected,
    onConversationEnded,
    onErrorChange,
    onFallbackToManual,
    onVoiceModeChange,
    signupRequestId,
    transcript,
  });
  const composer = useConversationComposer({
    isConnected: session.isConnected,
    isVoiceMode,
    sendMessage: session.sendMessage,
    sendUserActivity: session.sendUserActivity,
  });
  const uiState = getConversationUiState({
    isConnected: session.isConnected,
    isSpeaking: session.isSpeaking,
    isTransitioning: session.isTransitioning,
    isVoiceMode,
    status: session.status,
  });

  const handleSwitchToManual = () => {
    session.handleBack();
    onSwitchToManual();
  };

  return (
    <>
      <Box sx={styles.completionHeader}>
        <Typography color="primary" sx={styles.completionTitle}>
          {strings.formatString(strings.signupAs, strings.professional)}
        </Typography>
        <Button
          aria-label={strings.switchToManualMode}
          size="small"
          sx={styles.manualModeButton}
          variant="outlined"
          onClick={handleSwitchToManual}
        >
          {strings.switchToManualMode}
        </Button>
      </Box>
      <Box sx={styles.container}>
        <ConversationHeader
          isConnected={session.isConnected}
          onBack={session.handleBack}
          statusLabel={uiState.statusLabel}
        />

        <Box sx={styles.conversationBody}>
          <ConversationMessages
            copiedMessageId={transcript.copiedMessageId}
            emptyStateDescription={uiState.emptyStateDescription}
            isClientToolProcessing={isClientToolProcessing}
            messages={transcript.messages}
            messagesEndRef={transcript.messagesEndRef}
            onCopyMessage={transcript.handleCopyMessage}
          />

          {error && (
            <Typography role="alert" sx={styles.error}>
              {error}
            </Typography>
          )}

          <ConversationComposer
            getInputByteFrequencyData={session.getInputByteFrequencyData}
            inputPlaceholder={uiState.inputPlaceholder}
            isConnected={session.isConnected}
            isTransitioning={session.isTransitioning}
            isVoiceMode={isVoiceMode}
            onInputKeyDown={composer.handleInputKeyDown}
            onSubmit={composer.handleSubmit}
            onTextInputChange={composer.handleTextInputChange}
            onTextModeButton={session.handleTextModeButton}
            onVoiceButton={session.handleVoiceButton}
            textInput={composer.textInput}
            textInputRef={composer.textInputRef}
            voiceActionLabel={uiState.voiceActionLabel}
            voiceStageRef={composer.voiceStageRef}
            voiceStatusLabel={uiState.voiceStatusLabel}
          />
        </Box>
      </Box>
    </>
  );
};

export default AiCompanionConversation;
